import React from 'react';
import { 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Grid,
  Divider,
  Box,
  TextField
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import EmailIcon from '@material-ui/icons/Email';
import PhoneIcon from '@material-ui/icons/Phone';
import ChatIcon from '@material-ui/icons/Chat';
import SecurityIcon from '@material-ui/icons/Security';
import HelpIcon from '@material-ui/icons/Help';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

const styles = theme => ({
  root: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  header: {
    backgroundColor: '#1976d2',
    color: 'white',
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(3),
    textAlign: 'center'
  },
  card: {
    marginBottom: theme.spacing(2),
    '&:hover': {
      elevation: 8,
      transform: 'translateY(-2px)',
      transition: 'all 0.3s ease'
    }
  },
  contactMethod: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f0f0f0'
    }
  },
  icon: {
    marginRight: theme.spacing(2),
    fontSize: '2rem'
  },
  urgentCard: {
    backgroundColor: '#fff3e0',
    border: '2px solid #ff9800'
  },
  securityCard: {
    backgroundColor: '#e8f5e8',
    border: '2px solid #4caf50'
  },
  backButton: {
    marginBottom: theme.spacing(2)
  }
});

class SupportContact extends React.Component {
  state = {
    userEmail: '',
    lockoutCount: 0,
    issueType: 'security_lockout',
    // Email verification states
    showEmailVerification: false,
    verificationEmail: '',
    verificationCode: '',
    codeSent: false
  };

  componentDidMount() {
    // Get email from URL params or localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get('email');
    const lockoutParam = urlParams.get('lockout');
    
    if (emailParam) {
      this.setState({ userEmail: emailParam });
    }
    
    if (lockoutParam) {
      this.setState({ lockoutCount: parseInt(lockoutParam) || 0 });
    }
  }

  handleEmailContact = () => {
    const { userEmail, lockoutCount } = this.state;
    const subject = encodeURIComponent('Security Lockout Escalation - Urgent Assistance Required');
    const body = encodeURIComponent(
      `Dear Me-Vision Academy Support Team,

I am experiencing a security lockout issue with my account and need urgent assistance.

Account Details:
- Email: ${userEmail || 'Please enter your email'}
- Issue: Multiple security lockouts (${lockoutCount} times)
- Issue Type: WhatsApp OTP verification lockout
- Date/Time: ${new Date().toLocaleString()}

Description:
My account has been locked multiple times due to failed WhatsApp OTP verification attempts. I need to regain access to my account and require assistance with the security verification process.

Please help me resolve this issue as soon as possible.

Thank you for your assistance.

Best regards,
[Your Name]`
    );
    
    window.location.href = `mailto:support@mevisionacademy.com?subject=${subject}&body=${body}`;
  };

  // Email verification handlers
  handleEmailVerificationRequest = () => {
    if (!this.state.verificationEmail || !this.state.verificationEmail.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    // Simulate sending verification code
    
    
    // For now, just show success message (skeleton implementation)
    this.setState({ codeSent: true });
    alert(`✅ Verification code sent to ${this.state.verificationEmail}\n\n(This is a placeholder - email verification not implemented yet)`);
  };

  handleVerificationCodeSubmit = () => {
    if (!this.state.verificationCode || this.state.verificationCode.length < 6) {
      alert('Please enter a valid verification code');
      return;
    }

    // Simulate code verification
    
    
    // For now, just show success message (skeleton implementation)
    alert(`✅ Email verification successful!\n\n(This is a placeholder - verification not implemented yet)`);
    
    // Reset verification state
    this.setState({
      showEmailVerification: false,
      verificationEmail: '',
      verificationCode: '',
      codeSent: false
    });
  };

  handleInputChange = (field) => (event) => {
    this.setState({
      [field]: event.target.value
    });
  };

  handleBackToLogin = () => {
    window.location.href = '/';
  };

  copyEmailToClipboard = () => {
    navigator.clipboard.writeText('support@mevisionacademy.com')
      .then(() => {
        alert('✅ Email address copied to clipboard!');
      })
      .catch(() => {
        alert('❌ Failed to copy email address');
      });
  };

  render() {
    const { classes } = this.props;
    const { userEmail, lockoutCount } = this.state;

    return (
      <Container maxWidth="md" className={classes.root}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={this.handleBackToLogin}
          className={classes.backButton}
        >
          Back to Login
        </Button>

        <div className={classes.header}>
          <SecurityIcon style={{ fontSize: '3rem', marginBottom: '1rem' }} />
          <Typography variant="h4" gutterBottom>
            🆘 Support & Contact
          </Typography>
          <Typography variant="h6">
            Security Lockout Assistance
          </Typography>
          {userEmail && (
            <Typography variant="body1" style={{ marginTop: '1rem' }}>
              Account: {userEmail}
            </Typography>
          )}
          {lockoutCount > 0 && (
            <Typography variant="body1" style={{ marginTop: '0.5rem' }}>
              Lockout Count: {lockoutCount} times
            </Typography>
          )}
        </div>

        <Grid container spacing={3}>
          {/* Urgent Support Card */}
          <Grid item xs={12}>
            <Card className={`${classes.card} ${classes.urgentCard}`}>
              <CardContent>
                <Typography variant="h6" style={{ color: '#e65100', fontWeight: 'bold' }}>
                  🚨 Urgent Security Lockout Assistance
                </Typography>
                <Typography variant="body2" style={{ margin: '1rem 0' }}>
                  Your account has been locked multiple times due to security reasons. 
                  We're here to help you regain access quickly and securely.
                </Typography>
                <Divider style={{ margin: '1rem 0' }} />
                
                <div className={classes.contactMethod} onClick={this.handleEmailContact}>
                  <EmailIcon className={classes.icon} style={{ color: '#1976d2' }} />
                  <div>
                    <Typography variant="h6">Email Support</Typography>
                    <Typography variant="body2" color="textSecondary">
                      support@mevisionacademy.com - Detailed assistance with your lockout
                    </Typography>
                  </div>
                </div>
                
                <Divider />
                
                <div 
                  className={classes.contactMethod} 
                  onClick={() => this.setState({ showEmailVerification: true })}
                >
                  <EmailIcon className={classes.icon} style={{ color: '#4caf50' }} />
                  <div>
                    <Typography variant="h6">Email Verification</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Verify your identity via email to regain account access
                    </Typography>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Grid>

          {/* Email Verification Section */}
          {this.state.showEmailVerification && (
            <Grid item xs={12}>
              <Card className={classes.card}>
                <CardContent>
                  <Typography variant="h6" style={{ color: '#4caf50', fontWeight: 'bold', marginBottom: '1rem' }}>
                    📧 Email Verification
                  </Typography>
                  
                  {!this.state.codeSent ? (
                    <div>
                      <Typography variant="body2" style={{ marginBottom: '1rem' }}>
                        Enter your email address to receive a verification code:
                      </Typography>
                      
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Enter your email address"
                        value={this.state.verificationEmail}
                        onChange={this.handleInputChange('verificationEmail')}
                        style={{ marginBottom: '1rem' }}
                      />
                      
                      <div style={{ display: 'flex', gap: '1rem' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={this.handleEmailVerificationRequest}
                        >
                          Send Verification Code
                        </Button>
                        
                        <Button
                          variant="outlined"
                          onClick={() => this.setState({ showEmailVerification: false })}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Typography variant="body2" style={{ marginBottom: '1rem' }}>
                        Enter the verification code sent to {this.state.verificationEmail}:
                      </Typography>
                      
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Enter 6-digit verification code"
                        value={this.state.verificationCode}
                        onChange={this.handleInputChange('verificationCode')}
                        inputProps={{
                          maxLength: 6,
                          style: { textAlign: 'center', fontSize: '18px', letterSpacing: '2px' }
                        }}
                        style={{ marginBottom: '1rem' }}
                      />
                      
                      <div style={{ display: 'flex', gap: '1rem' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={this.handleVerificationCodeSubmit}
                        >
                          Verify Code
                        </Button>
                        
                        <Button
                          variant="outlined"
                          onClick={this.handleEmailVerificationRequest}
                        >
                          Resend Code
                        </Button>
                        
                        <Button
                          variant="text"
                          onClick={() => this.setState({ 
                            showEmailVerification: false, 
                            codeSent: false,
                            verificationEmail: '',
                            verificationCode: ''
                          })}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Security Information Card */}
          <Grid item xs={12} md={6}>
            <Card className={`${classes.card} ${classes.securityCard}`}>
              <CardContent>
                <Typography variant="h6" style={{ color: '#2e7d32', fontWeight: 'bold' }}>
                  🛡️ Security Lockout Information
                </Typography>
                <Typography variant="body2" style={{ margin: '1rem 0' }}>
                  <strong>Why did this happen?</strong><br />
                  • 3 failed WhatsApp OTP attempts = 30-minute lockout<br />
                  • 3+ lockouts = Escalation required<br />
                  • This protects your account from unauthorized access
                </Typography>
                <Typography variant="body2">
                  <strong>What happens next?</strong><br />
                  • Our support team will verify your identity<br />
                  • We'll help you regain secure access<br />
                  • Account security settings may be reviewed
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Actions Card */}
          <Grid item xs={12} md={6}>
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                  ⚡ Quick Actions
                </Typography>
                <Box mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={this.handleEmailContact}
                    style={{ marginBottom: '1rem' }}
                  >
                    📧 Send Email Report
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={this.copyEmailToClipboard}
                    style={{ marginBottom: '1rem' }}
                  >
                    📋 Copy Email Address
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={this.handleBackToLogin}
                  >
                    🔙 Back to Login
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Business Hours Card */}
          <Grid item xs={12}>
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                  🕒 Support Hours & Response Times
                </Typography>
                <Grid container spacing={2} style={{ marginTop: '1rem' }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <strong>Email Support:</strong><br />
                      24/7 - Response within 2-4 hours
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <strong>Email Verification:</strong><br />
                      Available 24/7<br />
                      Instant verification process
                    </Typography>
                  </Grid>
                </Grid>
                
                <Typography variant="body2" style={{ marginTop: '1rem', fontStyle: 'italic', color: '#6c757d' }}>
                  For urgent issues, please use email support with "URGENT" in the subject line.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    );
  }
}

export default withStyles(styles)(SupportContact); 